"use client";

/**
 * RevealText 컴포넌트
 * @description 글자가 낱말 단위로 차례로 떠오른다 — 표제에 쓴다. 낱말마다 상자를 씌우고
 *   아래에서 올라오게 하되, 상자가 넘치는 부분을 잘라 **가려졌다 드러나는** 느낌을 낸다.
 *
 *   읽어주는 쪽에는 한 문장 그대로다(`aria-label`) — 낱말 상자는 장식이라 숨긴다. 움직임을
 *   줄인 설정에서는 처음부터 그 자리에 서 있다.
 * @example
 * <RevealText as="h1" text={post.title} className={styles.title} />
 */
import { memo, useRef } from "react";

import type { RevealTextProps } from "./RevealText.types";

import { gsap, useGSAP } from "@/shared/lib/gsap";

/** 낱말 사이 간격(초) — 표제 여섯 낱말이면 0.3초 안에 다 선다 */
const STAGGER_S = 0.05;
const DURATION_S = 0.8;

const RevealText = memo<RevealTextProps>(
  ({ text, as: Tag = "span", className, delay = 0 }) => {
    const ref = useRef<HTMLElement>(null);
    const words = text.split(/\s+/).filter(Boolean);

    useGSAP(
      () => {
        const el = ref.current;
        if (!el) return;
        const mm = gsap.matchMedia();
        mm.add("(prefers-reduced-motion: no-preference)", () => {
          gsap.fromTo(
            el.querySelectorAll("[data-word]"),
            { yPercent: 110, opacity: 0 },
            {
              yPercent: 0,
              opacity: 1,
              delay,
              duration: DURATION_S,
              stagger: STAGGER_S,
              ease: "power3.out",
              clearProps: "transform,opacity",
            },
          );
        });
        return () => mm.revert();
      },
      { scope: ref, dependencies: [text, delay] },
    );

    return (
      <Tag ref={ref} className={className} aria-label={text}>
        {words.map((word, index) => (
          /* 낱말 사이 빈칸은 상자 **밖**에 둔다 — 상자 안 끝의 빈칸은 접혀 사라진다 */
          <span key={`${word}-${index}`} aria-hidden>
            {index > 0 ? " " : null}
            <span className="inline-block overflow-hidden align-bottom">
              <span data-word className="inline-block">
                {word}
              </span>
            </span>
          </span>
        ))}
      </Tag>
    );
  },
);

RevealText.displayName = "RevealText";

export default RevealText;
